import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { X, Loader2 } from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  useCreateInvoiceMutation,
  useGetNextInvoiceNoQuery,
} from "@/features/api/invoiceApi";
import { toast } from "sonner";

const CreateInvoicePage = () => {
  const invoiceRef = useRef(null);
  const navigate = useNavigate();

  const [createInvoice, { isLoading, error: createError }] = useCreateInvoiceMutation();
  const {
    data: nextInvoiceData,
    isLoading: isFetchingInvoiceNo,
    error: invoiceNoError,
    isError: isInvoiceNoError,
  } = useGetNextInvoiceNoQuery();

  const [isSaved, setIsSaved] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const [formData, setFormData] = useState({
    companyName: "",
    clientName: "",
    address: "",
    country: "",
    pin: "",
    phone: "",
    email: "",
    invoiceNo: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    paymentMethod: "Cash",
    upiRef: "",
    // items: description, duration, quantity, rate, amount
    items: [{ description: "", duration: "", quantity: 1, rate: 0, amount: 0 }],
    applySgst: false,
    applyCgst: false,
  });

  const [errors, setErrors] = useState({});

  // Handle invoice number from API
  useEffect(() => {
    if (nextInvoiceData?.success && nextInvoiceData?.invoiceNo !== undefined && nextInvoiceData?.invoiceNo !== null) {
      const formattedInvoiceNo = String(nextInvoiceData.invoiceNo).padStart(2, "0");
      setFormData((prev) => ({ ...prev, invoiceNo: formattedInvoiceNo }));
    } else if (nextInvoiceData && !nextInvoiceData.success) {
      console.error("Invoice number fetch failed:", nextInvoiceData.message);
      toast.error(nextInvoiceData.message || "Failed to retrieve invoice number");
      setFormData((prev) => ({ ...prev, invoiceNo: "" }));
    }

    if (isInvoiceNoError) {
      console.error("Invoice number error:", invoiceNoError);
      toast.error(
        invoiceNoError?.data?.message ||
        invoiceNoError?.message ||
        "Error retrieving invoice number"
      );
      setFormData((prev) => ({ ...prev, invoiceNo: "" }));
    }

    if (!isFetchingInvoiceNo && !nextInvoiceData && !isInvoiceNoError) {
      // still okay — keep invoiceNo blank until API provides one
      setFormData((prev) => ({ ...prev, invoiceNo: "" }));
    }
  }, [nextInvoiceData, invoiceNoError, isInvoiceNoError, isFetchingInvoiceNo]);

  useEffect(() => {
    if (createError) {
      console.error("Create invoice error:", createError);
      const errorMessage =
        createError?.data?.message ||
        createError?.message ||
        `Failed to create invoice (Status: ${createError?.status || "Unknown"}).`;
      toast.error(errorMessage);
    }
  }, [createError]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  // handle item changes — supports rate, quantity, amount, description, duration
  const handleItemChange = (idx, field, rawValue) => {
    setFormData((prev) => {
      const updatedItems = prev.items.map((it, i) => ({ ...it }));
      const value =
        field === "rate" || field === "quantity" || field === "amount"
          ? Number(rawValue) || 0
          : rawValue;

      updatedItems[idx][field] = value;

      // auto-calc amount if rate or quantity changed
      if (field === "rate" || field === "quantity") {
        const rate = Number(updatedItems[idx].rate) || 0;
        const quantity = Number(updatedItems[idx].quantity) || 0;
        updatedItems[idx].amount = Number((rate * quantity).toFixed(2));
      }

      return { ...prev, items: updatedItems };
    });
    setErrors((prev) => ({ ...prev, items: null }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: "", duration: "", quantity: 1, rate: 0, amount: 0 }],
    }));
  };

  const removeItem = (idx) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      // If amount exists and is numeric, use it; otherwise use rate * quantity
      const amt = (typeof item.amount === "number" && !Number.isNaN(item.amount))
        ? item.amount
        : (Number(item.rate) || 0) * (Number(item.quantity) || 0);
      return sum + amt;
    }, 0);

    const sgst = formData.applySgst ? subtotal * 0.09 : 0;
    const cgst = formData.applyCgst ? subtotal * 0.09 : 0;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      sgst: Number(sgst.toFixed(2)),
      cgst: Number(cgst.toFixed(2)),
      total: Number((subtotal + sgst + cgst).toFixed(2)),
    };
  };

  const { subtotal, sgst, cgst, total } = calculateTotals();

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "N/A";

  const formatCurrency = (amount = 0) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(Number(amount) || 0);

  const validate = () => {
    const e = {};
    // if (!formData.companyName) e.companyName = "Company name is required";
    if (!formData.clientName) e.clientName = "Client name is required";
    if (!formData.address) e.address = "Address is required";
    if (!formData.country) e.country = "Country is required";
    if (!formData.invoiceNo) e.invoiceNo = "Invoice number is required";

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      e.email = "Invalid email format";
    }
    if (formData.phone && !/^[6-9]\d{9}$/.test(formData.phone)) {
      e.phone = "Invalid phone number (must be 10 digits starting with 6-9)";
    }
    if (formData.pin && !/^\d{6}$/.test(formData.pin)) {
      e.pin = "Invalid pin code (must be 6 digits)";
    }

    // items validation
    if (!Array.isArray(formData.items) || formData.items.length === 0) {
      e.items = "At least one item is required";
    } else {
      const badIndex = formData.items.findIndex(
        (it) =>
          !it.description ||
          Number(it.quantity) < 0 ||
          Number(it.rate) < 0 ||
          Number(it.amount) < 0
      );
      if (badIndex !== -1) {
        e.items = "All items must have a description and non-negative numeric values for rate/quantity/amount";
      }
    }

    if ((formData.paymentMethod === "UPI" || formData.paymentMethod === "Other") && !formData.upiRef) {
      e.upiRef = `${formData.paymentMethod} reference number is required`;
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Generate PDF and return a Blob (so we can append it to FormData)
  const generatePdfBlob = async () => {
    setIsPrinting(true);

    try {
      // small delay so print-only styles can apply
      await new Promise((resolve) => setTimeout(resolve, 100));
      const element = invoiceRef.current;
      if (!element) throw new Error("Invoice element not found");

      // Toggle visibility for screen-only / print-only
      const screenOnlyEls = element.querySelectorAll(".screen-only");
      const printOnlyEls = element.querySelectorAll(".print-only");

      screenOnlyEls.forEach((el) => (el.style.display = "none"));
      printOnlyEls.forEach((el) => (el.style.display = "block"));

      // Force reflow before capture
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const canvas = await html2canvas(element, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
        windowWidth: document.body.scrollWidth || document.documentElement.scrollWidth,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      // Return blob so createFormData can make a File
      const blob = pdf.output("blob");
      return blob;
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF. Please try again.");
      return null;
    } finally {
      // Revert visibility
      const element = invoiceRef.current;
      if (element) {
        const screenOnlyEls = element.querySelectorAll(".screen-only");
        const printOnlyEls = element.querySelectorAll(".print-only");

        screenOnlyEls.forEach((el) => (el.style.display = ""));
        printOnlyEls.forEach((el) => (el.style.display = "none"));
      }
      setIsPrinting(false);
    }
  };

  // Create FormData to send to the API. Accepts optional pdfBlob (Blob).
  const createFormData = (pdfBlob = null) => {
    const form = new FormData();
    form.append("invoiceNo", formData.invoiceNo);
    form.append("invoiceDate", formData.invoiceDate);
    form.append("paymentMethod", formData.paymentMethod);
    form.append(
      "upiRef",
      formData.paymentMethod === "UPI" || formData.paymentMethod === "Other"
        ? formData.upiRef
        : ""
    );
    form.append(
      "billTo",
      JSON.stringify({
        companyName: formData.companyName,
        clientName: formData.clientName,
        address: formData.address,
        country: formData.country,
        pinCode: formData.pin || null,
        phone: formData.phone || null,
        email: formData.email || null,
      })
    );
    form.append("items", JSON.stringify(formData.items));
    form.append("subtotal", subtotal.toFixed(2));
    form.append("sgst", sgst.toFixed(2));
    form.append("cgst", cgst.toFixed(2));
    form.append("total", total.toFixed(2));
    if (pdfBlob) {
      const file = new File([pdfBlob], `${formData.invoiceNo || "invoice"}.pdf`, {
        type: "application/pdf",
      });
      form.append("pdf", file);
    }
    return form;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please fix the form errors before saving.");
      return;
    }

    try {
      const form = createFormData();
      await createInvoice(form).unwrap();
      setIsSaved(true);
      toast.success("Invoice saved successfully!");
    } catch (err) {
      console.error("Save invoice error:", err);
      const errorMessage =
        err?.data?.message ||
        err?.error ||
        err?.message ||
        `Failed to save invoice (Status: ${err?.status || "Unknown"}).`;
      toast.error(errorMessage);
    }
  };

  const handleGeneratePDF = async () => {
    if (!validate()) {
      toast.error("Please fix the form errors before generating the PDF.");
      return;
    }

    const blob = await generatePdfBlob();
    if (!blob) return;

    try {
      const form = createFormData(blob);

      if (!isSaved) {
        await createInvoice(form).unwrap();
        setIsSaved(true);
        toast.success("Invoice saved successfully before PDF generation.");
      }

      // trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${formData.invoiceNo || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success("Invoice PDF downloaded successfully!");
    } catch (err) {
      console.error("Generate PDF error:", err);
      const errorMessage =
        err?.data?.message ||
        err?.error ||
        err?.message ||
        `Failed to generate and save PDF (Status: ${err?.status || "Unknown"}).`;
      toast.error(errorMessage);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto p-6">
      {isFetchingInvoiceNo && (
        <div className="flex justify-center items-center py-4">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Loading invoice number...</span>
        </div>
      )}

      <div
        ref={invoiceRef}
        className={`bg-white p-6 text-black ${isPrinting ? "printing" : ""}`}
      >
        {/* Header */}
        <div className="flex justify-between mb-6">
          <div className="space-y-2 text-sm">
            <img
              src="/image/logo/fetc-logo.png"
              alt="Logo"
              className="w-24 h-auto max-h-24 object-contain rounded"
            />
            <p className="font-semibold">Gina Abroad Pvt. Ltd</p>
            <p>Bhumika Dilkhush</p>
            <p>238-239, Roongta Signature, VIP Road, Vesu, Surat - 395007</p>
            <p>Phone: +91-9033347204</p>
            <p>Email: accounts@fetc.in</p>
            <p>GST Number: 24AAKCG7584N1ZU</p>
          </div>

          <div className="text-right text-sm space-y-2">
            <h2 className="text-2xl font-bold">INVOICE</h2>
            <p>
              <strong>Invoice No:</strong> {formData.invoiceNo || ""}
            </p>
            <div className="screen-only">
              <Input
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => handleInputChange("invoiceDate", e.target.value)}
                className="mt-1"
                aria-label="Invoice Date"
                disabled={isFetchingInvoiceNo}
              />
            </div>
            <div className="print-only">
              <p>
                <strong>Date:</strong> {formatDate(formData.invoiceDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="mb-6">
          <CardTitle className="text-lg mb-2">Bill To</CardTitle>

          <div className="screen-only grid grid-cols-2 gap-4">
            <div>
              <Input
                placeholder="Company Name"
                value={formData.companyName}
                onChange={(e) => handleInputChange("companyName", e.target.value)}
                aria-label="Company Name"
                disabled={isFetchingInvoiceNo}
              />
              {errors.companyName && <p className="text-red-500 text-sm">{errors.companyName}</p>}
            </div>

            <div>
              <Input
                placeholder="Client Name"
                value={formData.clientName}
                onChange={(e) => handleInputChange("clientName", e.target.value)}
                aria-label="Client Name"
                disabled={isFetchingInvoiceNo}
              />
              {errors.clientName && <p className="text-red-500 text-sm">{errors.clientName}</p>}
            </div>

            <div>
              <Input
                placeholder="Address"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                aria-label="Address"
                disabled={isFetchingInvoiceNo}
              />
              {errors.address && <p className="text-red-500 text-sm">{errors.address}</p>}
            </div>

            <div>
              <Input
                placeholder="Country"
                value={formData.country}
                onChange={(e) => handleInputChange("country", e.target.value)}
                aria-label="Country"
                disabled={isFetchingInvoiceNo}
              />
              {errors.country && <p className="text-red-500 text-sm">{errors.country}</p>}
            </div>

            <div>
              <Input
                placeholder="Pin Code"
                value={formData.pin}
                onChange={(e) => handleInputChange("pin", e.target.value)}
                aria-label="Pin Code"
                disabled={isFetchingInvoiceNo}
              />
              {errors.pin && <p className="text-red-500 text-sm">{errors.pin}</p>}
            </div>

            <div>
              <Input
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                aria-label="Phone Number"
                disabled={isFetchingInvoiceNo}
              />
              {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
            </div>

            <div>
              <Input
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                aria-label="Email Address"
                disabled={isFetchingInvoiceNo}
              />
              {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
            </div>
          </div>

          <div className="print-only grid grid-cols-2 gap-4 text-sm">
            {formData.companyName && <p><strong>Company Name:</strong> {formData.companyName}</p>}
            {formData.clientName && <p><strong>Client Name:</strong> {formData.clientName}</p>}
            {formData.address && <p><strong>Address:</strong> {formData.address}</p>}
            {formData.country && <p><strong>Country:</strong> {formData.country}</p>}
            {formData.pin && <p><strong>Pin Code:</strong> {formData.pin}</p>}
            {formData.phone && <p><strong>Phone:</strong> {formData.phone}</p>}
            {formData.email && <p><strong>Email:</strong> {formData.email}</p>}
          </div>
        </div>

        {/* Items */}
        <div>
          {/* Header */}
          <div className="grid grid-cols-12 font-semibold border-b py-2 text-sm">
            <p className="col-span-4">Description</p>
            <p className="col-span-2 text-center">Duration</p>
            <p className="col-span-2 text-center">Quantity</p>
            <p className="col-span-3 text-right">Amount</p>
            <div className="col-span-1 screen-only text-center">Action</div>
          </div>

          {/* Rows */}
          {formData.items.map((item, i) => (
            <div
              key={i}
              className="grid grid-cols-12 border-b py-2 text-sm items-center"
            >
              {/* Description */}
              <div className="col-span-4">
                <div className="screen-only">
                  <Input
                    value={item.description}
                    onChange={(e) => handleItemChange(i, "description", e.target.value)}
                    placeholder="Description"
                    aria-label={`Item ${i + 1} Description`}
                    disabled={isFetchingInvoiceNo}
                  />
                </div>
                <div className="print-only">
                  <p>{item.description || "N/A"}</p>
                </div>
              </div>

              {/* Duration */}
              <div className="col-span-2 text-center">
                <div className="screen-only">
                  <Input
                    type="text"
                    value={item.duration}
                    onChange={(e) => handleItemChange(i, "duration", e.target.value)}
                    placeholder="Duration (e.g. 2h, 3 days)"
                    aria-label={`Item ${i + 1} Duration`}
                    disabled={isFetchingInvoiceNo}
                  />
                </div>
                <div className="print-only">
                  <p>{item.duration || "N/A"}</p>
                </div>
              </div>

              {/* Quantity */}
              <div className="col-span-2 text-center">
                <div className="screen-only">
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(i, "quantity", e.target.value)}
                    min="0"
                    placeholder="Qty"
                    aria-label={`Item ${i + 1} Quantity`}
                    disabled={isFetchingInvoiceNo}
                  />
                </div>
                <div className="print-only">
                  <p>{item.quantity ?? 0}</p>
                </div>
              </div>

              {/* Amount */}
              <div className="col-span-3 text-right">
                <div className="screen-only">
                  <Input
                    type="number"
                    value={item.amount}
                    onChange={(e) => handleItemChange(i, "amount", e.target.value)}
                    min="0"
                    placeholder="Amount"
                    aria-label={`Item ${i + 1} Amount`}
                    disabled={isFetchingInvoiceNo}
                  />
                </div>
                <div className="print-only">
                  <p>{formatCurrency(item.amount)}</p>
                </div>
              </div>

              {/* Delete button */}
              <div className="col-span-1 flex justify-center screen-only">
                <button
                  type="button"
                  onClick={() => removeItem(i)}
                  className="text-red-500 hover:text-red-700 font-bold"
                >
                  ×
                </button>
              </div>
            </div>
          ))}

          {/* Error message */}
          {errors.items && (
            <p className="text-red-500 text-sm screen-only">{errors.items}</p>
          )}

          <div className="screen-only mt-2">
            <Button onClick={addItem} disabled={isFetchingInvoiceNo}>
              + Add Item
            </Button>
          </div>

          {formData.items.length > 1 && (
            <div className="screen-only mt-2 flex gap-2 flex-wrap">
              {formData.items.map((_, i) => (
                <Button
                  key={i}
                  variant="ghost"
                  size="icon"
                  className="remove-btn"
                  onClick={() => removeItem(i)}
                  aria-label={`Remove Item ${i + 1}`}
                  disabled={isFetchingInvoiceNo}
                >
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Totals & Payment Method */}
        <div className="mt-6 flex flex-col gap-2 text-sm">
          <div className="flex flex-wrap items-center gap-2 print-only">
            <p><strong>Payment Method:</strong> {formData.paymentMethod}</p>
            {(formData.paymentMethod === "UPI" || formData.paymentMethod === "Other") &&
              formData.upiRef && (
                <p><strong>{formData.paymentMethod} Ref Number:</strong> {formData.upiRef}</p>
              )}
          </div>

          <div className="screen-only flex flex-wrap items-center gap-2">
            <label htmlFor="payment-method" className="font-semibold">
              Payment Method:
            </label>
            <select
              id="payment-method"
              className="border rounded px-2 py-1"
              value={formData.paymentMethod}
              onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
              disabled={isFetchingInvoiceNo}
            >
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Netbanking">Netbanking</option>
              <option value="Cheque">Cheque</option>
              <option value="Other">Other</option>
            </select>

            {(formData.paymentMethod === "UPI" || formData.paymentMethod === "Other") && (
              <>
                <Input
                  className="ml-2 min-w-[200px] max-w-xs"
                  placeholder={`${formData.paymentMethod} Reference Number`}
                  value={formData.upiRef}
                  onChange={(e) => handleInputChange("upiRef", e.target.value)}
                  disabled={isFetchingInvoiceNo}
                />
                {errors.upiRef && (
                  <p className="text-red-500 text-sm">{errors.upiRef}</p>
                )}
              </>
            )}
          </div>

          <div className="mt-6 flex justify-between items-start text-sm">
            {/* Bank Details */}
            <div>
              <p className="font-semibold">Bank Details:</p>
              <p>Bank: ICICI Bank</p>
              <p>A/C No.: 138605501228</p>
              <p>Name: Gina Abroad Pvt. Ltd.</p>
              <p>IFSC: ICIC0001386</p>
            </div>

            {/* Totals */}
            <div className="text-right">
              <p>Subtotal: {formatCurrency(subtotal)}</p>

              {/* SGST */}
              <div className="flex justify-end items-center gap-2 screen-only">
                <input
                  type="checkbox"
                  id="apply-sgst"
                  checked={formData.applySgst}
                  onChange={(e) => handleInputChange("applySgst", e.target.checked)}
                />
                <label htmlFor="apply-sgst">Apply SGST (9%)</label>
              </div>
              {formData.applySgst && (
                <p className="print-only">SGST (9%): {formatCurrency(sgst)}</p>
              )}

              {/* CGST */}
              <div className="flex justify-end items-center gap-2 screen-only">
                <input
                  type="checkbox"
                  id="apply-cgst"
                  checked={formData.applyCgst}
                  onChange={(e) => handleInputChange("applyCgst", e.target.checked)}
                />
                <label htmlFor="apply-cgst">Apply CGST (9%)</label>
              </div>
              {formData.applyCgst && (
                <p className="print-only">CGST (9%): {formatCurrency(cgst)}</p>
              )}

              <p className="font-bold text-base mt-2">
                Total Amount: {formatCurrency(total)}
              </p>
            </div>
          </div>
        </div>

        {/* Signature */}
        <div className="mt-10 flex justify-between items-start">
          <div className="flex flex-col items-center text-center">
            <div className="space-y-1">
              <p className="font-semibold">For, Foreign English Tests Capital</p>
              <p className="font-semibold">Powered by Gina Abroad Pvt. Ltd</p>
            </div>
            <img
              src="/image/gina-abroad-stamp.png"
              alt="Stamp"
              className="w-32 h-auto object-contain mt-2"
            />
          </div>

          <div className="flex flex-col items-end text-right space-y-2">
            <p className="font-semibold">Receiver's Signature</p>
            <div className="border-t border-gray-400 w-48"></div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-4">
        <Button
          variant="outline"
          onClick={() => navigate("/admin/invoices")}
          disabled={isLoading || isPrinting || isFetchingInvoiceNo}
        >
          Back
        </Button>

        <Button
          onClick={handleSave}
          disabled={isLoading || isPrinting || isFetchingInvoiceNo || isSaved || !formData.invoiceNo}
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Save
        </Button>

        {isSaved && (
          <Button
            onClick={handleGeneratePDF}
            disabled={isLoading || isPrinting || isFetchingInvoiceNo}
          >
            {isPrinting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Generate PDF
          </Button>
        )}
      </div>
    </Card>
  );
};

export default CreateInvoicePage;
